import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { getNewsById } from '../features/news/newsSlice';
import Skeleton from '../components/Skeleton';
import { Helmet } from 'react-helmet-async';
import { getImageUrl } from '../utils/imageUrl';

const NewsDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { currentNews, isLoading } = useSelector((state) => state.news);

  useEffect(() => {
    dispatch(getNewsById(id));
  }, [dispatch, id]);

  const getCategoryColor = (category) => {
    const lowerCategory = category.toLowerCase();
    if (lowerCategory.includes('pengabdian')) return 'bg-green-50 text-green-700';
    if (lowerCategory.includes('pengembangan')) return 'bg-blue-50 text-blue-700';
    if (lowerCategory.includes('penelitian')) return 'bg-purple-50 text-purple-700';
    return 'bg-gray-100 text-gray-700';
  };

  if (isLoading) {
    return (
      <div className="py-12">
        <section className="container mx-auto px-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-8">
            <Skeleton className="h-96 w-full rounded-xl mb-8" />
            <div className="space-y-4">
              <Skeleton className="h-12 w-3/4 rounded-xl" />
              <Skeleton className="h-6 w-1/2 rounded-xl" />
              <div className="pt-4 space-y-2">
                <Skeleton className="h-6 w-full rounded-lg" />
                <Skeleton className="h-6 w-full rounded-lg" />
                <Skeleton className="h-6 w-3/4 rounded-lg" />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!currentNews) {
    return (
      <div className="py-12">
        <section className="container mx-auto px-4">
          <div className="text-center py-20">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Berita tidak ditemukan</h3>
            <Link to="/news" className="text-dark-blue-700 font-medium hover:text-dark-blue-800">
              Kembali ke berita
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const imageUrl = getImageUrl(currentNews.image);

  return (
    <div className="py-12">
      {currentNews && (
        <Helmet>
          <title>{currentNews.title} | Penerbit Literasiologi</title>
          <meta name="description" content={currentNews.content.substring(0, 200)} />
          <meta property="og:title" content={currentNews.title} />
          <meta property="og:description" content={currentNews.content.substring(0, 200)} />
          <meta property="og:image" content={imageUrl} />
          <meta property="og:type" content="article" />
          <meta name="twitter:title" content={currentNews.title} />
          <meta name="twitter:description" content={currentNews.content.substring(0, 200)} />
          <meta name="twitter:image" content={imageUrl} />
        </Helmet>
      )}
      <section className="container mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-sm text-gray-500">
            <li>
              <Link to="/" className="hover:text-dark-blue-700 transition-colors">Beranda</Link>
            </li>
            <li>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </li>
            <li>
              <Link to="/news" className="hover:text-dark-blue-700 transition-colors">Berita</Link>
            </li>
            <li>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </li>
            {currentNews.category && (
              <>
                <li>
                  <Link
                    to={`/news/${currentNews.category.toLowerCase().replace(/\s+/g, '-')}`}
                    className="hover:text-dark-blue-700 transition-colors"
                  >
                    {currentNews.category}
                  </Link>
                </li>
                <li>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </li>
              </>
            )}
            <li className="text-gray-900 font-medium truncate max-w-[200px]">
              {currentNews.title}
            </li>
          </ol>
        </nav>

        <article className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
          {imageUrl && (
            <div className="flex justify-center mb-8">
              <img
                src={imageUrl}
                alt={currentNews.title}
                className="w-full max-w-3xl aspect-video object-cover rounded-xl"
              />
            </div>
          )}

          <div className="flex items-center gap-3 mb-6">
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${getCategoryColor(currentNews.category)}`}>
              {currentNews.category}
            </span>
            {currentNews.createdAt && (
              <span className="text-gray-500 text-sm">
                {new Date(currentNews.createdAt).toLocaleDateString('id-ID', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            )}
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{currentNews.title}</h1>

          <div className="flex items-center gap-3 mb-8 text-gray-500">
            <span className="text-sm">Oleh {currentNews.author}</span>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="text-gray-700 leading-relaxed text-lg prose-content" dangerouslySetInnerHTML={{ __html: currentNews.content }} />
          </div>
        </article>
      </section>
    </div>
  );
};

export default NewsDetail;
